import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import { boxBlur } from './blur.js';
import { copyImage } from './combine.js';

export interface UnsharpOptions {
  /** 0 disables; 0.3-0.8 suits SR output on flat vector-style artwork. */
  amount?: number;
  radius?: number;
  /** Detail below this magnitude is left alone. */
  threshold?: number;
}

/** Thresholded unsharp mask. Alpha passes through untouched. */
export function unsharpMask(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  options: UnsharpOptions = {},
): void {
  const amount = options.amount ?? 0;
  if (amount <= 0) {
    if (src !== dst) copyImage(ctx, src, dst);
    return;
  }

  const blurred = ctx.image(src.width, src.height, src.format, 'unsharp-blur');
  boxBlur(ctx, src, blurred, Math.max(1, Math.round(options.radius ?? 2)), 2);

  ctx.dispatch(
    {
      module: 'sharpen.wgsl',
      defines: { DST_FORMAT: dst.format },
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'texture', format: blurred.format },
        { kind: 'storageTexture', format: dst.format },
        { kind: 'uniform' },
      ],
    },
    [
      src,
      blurred,
      dst,
      ctx.uniform(
        new Struct(16).i(0, dst.width, dst.height).f(8, amount, options.threshold ?? 0).bytes,
      ),
    ],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    'unsharp',
  );
  blurred.release();
}
